import { React, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    Box,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Button,
    Toolbar,
    TableContainer,
    MenuItem,
    Select,
    InputLabel,
    FormControl

} from '@mui/material';
import { useLoaderData } from "react-router-dom";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CustomizeDialog from '../../Components/CustomizeDialog';
import { CustomTitle, getESTTime, handleSessionOut, Loader } from '../../HelperFunctions/HelperFunctions';
import { exportCustoimizedFile, exportPDF, getCustomizeData } from '../../API/backend';
import { enqueueSnackbar, SnackbarProvider } from "notistack";



const FlashReport = () => {
    const { clientName, clientID, UID, corpId, pdfData = {}, showCustomScreen = true, token } = useLoaderData();

    const [tableData, setTableData] = useState({})
    const [customeDataSelect, setCustomeDataSelect] = useState({})
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [defaultDetails, setDefaultDetails] = useState(true);
    const [fileExport, setFileExport] = useState("none");
    const [fileExportData, setFileExportData] = useState({});
    const skipKeys = ['label', 'display_date', 'MonthFromDate', 'MonthToDate', 'YearFromDate', 'YearToDate'];
    const requiredKeys = ["corporation", "avail_rooms", "sold_rooms", "ooo_rooms"];
    const sections = ["sale_date", "month_to_date", "year_to_date"];
    const containerRef = useRef(null);
    const isSideBySide = tableData?.layout_type === "Sale date / MTD / YTD - Side by side" || "";



    useEffect(() => {
        if (showCustomScreen) {
            customizeData()
            setDefaultDetails(false)
        }
        else if (!showCustomScreen && Object.entries(pdfData).length > 0) {
            handleUpdatedData(pdfData)
        }

    }, []);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollLeft = 0;
            containerRef.current.scrollTop = 0;
        }
    }, [tableData]);

    const tableRefs = useRef([]);
    const [maxTableWidth, setMaxTableWidth] = useState(0);

    useLayoutEffect(() => {
        if (tableRefs.current.length) {
            const widths = tableRefs.current.map(ref => ref?.offsetWidth || 0);
            setMaxTableWidth(Math.max(...widths));
        }
    }, [tableData]);

    useEffect(() => {
        //  **  this useEffect is for auto height both corporation and side tables when zoom-in or zoom-out **
        if (!isSideBySide || !containerRef.current) return;
        const alignRowHeights = () => {
            if (!isSideBySide || !containerRef.current) return;

            const leftRows = containerRef.current.querySelectorAll('.corp-column table tbody tr');
            const rightRows = containerRef.current.querySelectorAll('.data-table table tbody tr');

            if (!leftRows.length || !rightRows.length) return;

            leftRows.forEach((leftRow, i) => {
                const rightRow = rightRows[i];
                if (rightRow) {
                    const rightHeight = rightRow.getBoundingClientRect().height;
                    leftRow.style.height = `${rightHeight}px`;
                }
            });
        };


        const observer = new ResizeObserver(() => {
            alignRowHeights();
        });

        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, [tableData, isSideBySide]);




    const customizeData = async () => {
        setLoading(true)

        try {
            const payload = {
                client_name: clientName,
                client_id: clientID,
                user_id: UID
            }
            const fetchData = await getCustomizeData(payload)
            if (fetchData.status === 200) {
                setCustomeDataSelect(fetchData.data)
                setOpen(true)
            } else {
                console.log("customizeData Failed")
            }

        } catch (error) {
            if (error.status === 401) {
                await handleSessionOut()
            }
            enqueueSnackbar("Error while getting Customized object", { variant: "error" })

        } finally {
            setLoading(false)
        }
    }

    const handleExportChange = async (event) => {

        try {
            if (Object.entries(fileExportData).length === 0) {
                enqueueSnackbar("Choose customizations befor export", { variant: "warning" });
                return
            }
            setLoading(true);
            const selectedFormat = event.target.value;
            setFileExport(selectedFormat);
            if (selectedFormat === "none") return;
            // const url = selectedFormat === "pdf" ? "convert-json-to-pdf/" : "export_flash-sale-format"
            // const exportData = await exportCustoimizedFile(url, fileExportData);
            const exportData = await (selectedFormat === "pdf" ? exportPDF(fileExportData) : exportCustoimizedFile(fileExportData));
            const contentType = exportData.headers['content-type'] || 'application/octet-stream';
            const contentDisposition = exportData.headers['content-disposition'];
            let filename = 'flashReport';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match && match[1]) {
                    filename = match[1];
                }
            }
            const blob = new Blob([exportData.data], { type: contentType });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            // const extension = contentType.includes('pdf')
            //     ? '.pdf'
            //     : contentType.includes('excel') || contentType.includes('spreadsheet')
            //         ? '.xlsx'
            //         : '';

            // link.download = `${filename}${extension}`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            enqueueSnackbar("Error while Export", { variant: "error" })
        } finally {
            setLoading(false);
            setFileExport("none");

        };

    };

    const handleUpdatedData = async (updatedData) => {
        if (updatedData.layout_type === "Sale date / MTD / YTD - Side by side") {
            const customeData = await cleanNestedKeys(updatedData)
            setTableData(customeData);
        } else {
            const ensureData = await ensureRequiredKeys(updatedData)
            setTableData(ensureData);
        }
        setOpen(false);
        setDefaultDetails(true);
        setLoading(false);
    };

    // { it's for to add stats to get fixed width of headers}
    const ensureRequiredKeys = (data) => {
        Object.entries(data?.data).forEach(([section, items]) => {
            if (Array.isArray(items)) {
                items.forEach(item => {
                    const sectionData = item[section];
                    if (sectionData && typeof sectionData === "object") {
                        requiredKeys.forEach(key => {
                            if (!(key in sectionData)) {
                                sectionData[key] = "";
                            }
                        });
                    }
                });
            }
        });

        return data;
    };


    /*{it's for remove corporation from month & year in side by side rendering}*/
    function cleanNestedKeys(dataObj) {

        const data = dataObj?.data;
        const keys = sections;
        const presentKeys = keys.filter(key => key in data);
        let keysToClean = [];
        if (presentKeys.length === 2) {
            keysToClean = [presentKeys[1]];
        } else if (presentKeys.length >= 3) {
            keysToClean = presentKeys.slice(1);
        }

        keysToClean.forEach(key => {
            const value = data[key];
            if (Array.isArray(value)) {
                data[key] = value.map(item => {
                    const newItem = { ...item };
                    if (newItem[key] && typeof newItem[key] === "object") {
                        newItem[key] = { ...newItem[key] };
                        delete newItem[key]["corporation"];
                    }
                    return newItem;
                });
            }
        });

        return dataObj;
    }




    const CustomTableCell = (
        {
            children,
            align = "left",
            colSpan = 1,
            sx = {},
            isLastInGroup,
            isMainHeader,
            isFirstHeader,
            height = 25
        }
    ) => {
        return (

            <TableCell align={align} colSpan={colSpan}
                sx={{
                    // border: "none",
                    border: !isMainHeader ? "1px solid lightgrey" : "none",
                    height: height,
                    // padding: "4px 2px 4px 2px",
                    padding: "4px 3px 4px 3px",
                    // borderRight: isLastInGroup ? "3px solid #f7f7f7" : "none",
                    borderRight: isMainHeader ? "3px solid #f7f7f7" : "none",
                    // borderRadius: isMainHeader ? "8px 8px 0px 0px" : "0px",
                    borderRadius:
                        isSideBySide && isMainHeader && isFirstHeader === Object.keys(tableData?.data || {})[0]
                            ? "0px 8px 0px 0px"
                            : isMainHeader
                                ? "8px 8px 0px 0px"
                                : "0px",
                    bgcolor: isFirstHeader === "sale_date" ? "#78d8d8"
                        : isFirstHeader === "month_to_date" ? "#f3c589"
                            : isFirstHeader === "year_to_date" ? "#d4e8f3"
                                : "white",
                    ...sx,
                    whiteSpace: "nowrap",
                }}
            >
                {children}
            </TableCell>
        );
    };


    const formatCellValue = (value) => {

        const formatNumber = (num) => {
            const parsed = parseFloat(num);
            if (Number.isNaN(parsed)) return num;

            return parsed.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });

        };
        const formatPercentOrNumber = (val) => {
            if (typeof val === 'string' && val.includes('%')) {
                const num = parseFloat(val.replace('%', ''));
                return `${formatNumber(num)}%`;
            }
            return `$${formatNumber(val)}`;
        };

        if (
            typeof value === 'object' &&
            value !== null &&
            'variance' in value &&
            'sign' in value
        ) {

            return (

                <Stack direction="row" alignItems="center" spacing={0.1} >
                    {'values' in value && (<div>{formatPercentOrNumber(value.values)}</div>)}
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ color: value.sign === '+' ? 'green' : 'red' }}
                        gap={0.1}
                    >

                        {value.sign === '+' && value.variance != null ? (
                            <ArrowDropUpIcon sx={{ fontSize: 16 }} />
                        ) :
                            value.sign === '-' ? (<ArrowDropDownIcon sx={{ fontSize: 16 }} />) : ""
                        }
                        {value.variance != null && <span>{formatPercentOrNumber(value.variance)}</span>}
                    </Stack>
                </Stack>
            );
        }
        if (typeof value === 'object' && value !== null) {
            return formatPercentOrNumber(value.values);
        }

        if (typeof value === 'string') {
            return value;
        }
        return formatPercentOrNumber(value)
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString("en-US");
    };

    const renderHeaderTitle = (mainKey, row) => {
        let title = CustomTitle(mainKey.toUpperCase());
        let dateInfo = "";

        if (mainKey === "sale_date") {
            title = row.label || title;
            if (row.display_date) {
                dateInfo = formatDate(row.display_date);
            }
        }

        if (mainKey === "month_to_date") {
            const from = row.MonthFromDate ? formatDate(row.MonthFromDate) : "";
            const to = row.MonthToDate ? formatDate(row.MonthToDate) : "";
            dateInfo = [from, to].filter(Boolean).join(" - ");
        }

        if (mainKey === "year_to_date") {
            const from = row.YearFromDate ? formatDate(row.YearFromDate) : "";
            const to = row.YearToDate ? formatDate(row.YearToDate) : "";
            dateInfo = [from, to].filter(Boolean).join(" - ");
        }

        return (
            <Stack direction="row" alignItems="center" gap={2} justifyContent="center">
                <Typography
                    fontSize={["Saledate", "Yesterday", "MONTH TO DATE", "YEAR TO DATE"].includes(title) ? 13 : 12}
                    fontWeight={["Saledate", "Yesterday", "MONTH TO DATE", "YEAR TO DATE"].includes(title) ? 700 : 600}

                >
                    {title === "OCCUPANCY" ? `${title.toUpperCase()} %` : title === "Saledate" ? "SALE DATE" : title.toUpperCase()}
                </Typography>
                {dateInfo && (
                    <Typography fontSize={10} fontWeight={700}>{`( ${dateInfo} )`}</Typography>
                )}
            </Stack>
        );
    };

    const getTextWidth = (text, font = "10.3px Poppins, sans-serif") => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        context.font = font;
        return context.measureText(text).width;
    }

    const stickyBorder = {
        "&::after": {
            content: '""',
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            width: "0.1px",
            backgroundColor: "lightgrey",
        }
    };



    return (
        <SnackbarProvider
            maxSnack={1}
            anchorOrigin={{
                vertical: "top",
                horizontal: "center",
            }}
        >

            <Stack height={"100vh"} gap={1.5} p={1} bgcolor="#f7f7f7"   >

                <Toolbar disableGutters sx={{ flexDirection: "column" }}>
                    <Typography fontWeight={700} fontSize={27} color='#333333'>
                        COMBINED DAILY FLASH REPORT
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={2} alignSelf={"flex-end"}>


                        {showCustomScreen &&
                            <>
                                <FormControl size="small">
                                    <Select
                                        value={fileExport}
                                        onChange={handleExportChange}
                                        displayEmpty
                                        variant="outlined"
                                        sx={{
                                            px: 1,
                                            bgcolor: '#b5dcf2',
                                            '& fieldset': { border: 'none' },
                                        }}
                                        renderValue={(selected) => {
                                            if (selected === 'none') {
                                                return "Export";
                                            }
                                            return selected;
                                        }}
                                    >
                                        {/* <MenuItem value="none">
                                            Export
                                        </MenuItem> */}
                                        <MenuItem value="pdf">PDF</MenuItem>
                                        <MenuItem value="excel">Excel</MenuItem>
                                    </Select>
                                </FormControl>



                                <Button
                                    variant="contained"
                                    size='small'
                                    disableElevation
                                    disableRipple
                                    sx={{
                                        px: 4,
                                        textTransform: "none",
                                        backgroundColor: "#b5dcf2",
                                        color: "#000",
                                    }}
                                    // onClick={() => setOpen(true)}
                                    onClick={customizeData}
                                >
                                    Customize
                                </Button>
                            </>
                        }

                    </Stack>
                </Toolbar>

                <Stack component={TableContainer} direction={isSideBySide ? "row" : "column"} gap={isSideBySide ? 0 : 3} ref={containerRef} height={"100%"}>
                    {
                         tableData?.data && Object.keys(tableData.data).length > 0 && !tableData.data.message ?
                            Object.keys(tableData.data).map((sectionKey, sectionIndex) => {

                                let lengths;

                                if (tableData?.selected_option === "Seperate by Actual / LY / Budget") {
                                    const targetKeys = ["actual", "last_year", "budget"];

                                    lengths = Object.keys(tableData.data).map(sectionKey => {
                                        const firstRow = tableData.data[sectionKey][0];

                                        const foundKey = targetKeys.find(k => k in firstRow);

                                        let subKeyLength = 0;
                                        if (foundKey && typeof firstRow[foundKey] === "object") {
                                            subKeyLength = Object.keys(firstRow[foundKey]).length;
                                        }

                                        return subKeyLength + 3;
                                    });
                                } else {
                                    lengths = Object.keys(tableData.data).map(
                                        key => Object.keys(tableData.data[key][0]).length
                                    );
                                }


                                const condition =
                                    Math.max(...lengths) - Math.min(...lengths) <= 2;

                                const firstStickyKey = Object.keys(tableData.data[sectionKey][0])[0];


                                const corporationNames = tableData.data[sectionKey]
                                    .map(row => row[firstStickyKey]?.corporation)
                                    .filter(Boolean);

                                const maxCorpLengthPx = Math.max(
                                    ...tableData.data[sectionKey].map(row =>
                                        getTextWidth(row[firstStickyKey]?.corporation || "")
                                    )
                                );
                                const corpTextWidth = Math.max(maxCorpLengthPx, 83);
                                const corpWidth = corpTextWidth + 5;

                                return (
                                    <>{/* left column freez for side by side */}
                                        {isSideBySide && sectionIndex === 0 && (
                                            <Box className="corp-column" sx={{
                                                minWidth: `${corpWidth}px`,
                                                position: "sticky",
                                                left: 0,
                                                zIndex: 10,

                                            }}>
                                                <Table size="small"
                                                >
                                                    <TableHead>

                                                        <TableRow sx={{
                                                            height: "27.8px",
                                                            position: "sticky",
                                                            top: 0,
                                                            zIndex: 4,
                                                        }}>
                                                            <CustomTableCell
                                                                isMainHeader
                                                                isFirstHeader={Object.keys(tableData.data)[0]}
                                                                sx={{
                                                                    borderRadius: "8px 0px 0px 0px",
                                                                    borderRight: "none"
                                                                }}
                                                            >
                                                            </CustomTableCell>
                                                        </TableRow>
                                                        <TableRow sx={{
                                                            height: "32.3px",
                                                            position: "sticky",
                                                            top: 27,
                                                            zIndex: 3,

                                                        }}>
                                                            <CustomTableCell sx={{ bgcolor: "#f5f5f5", border: 'none' }}> </CustomTableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <CustomTableCell
                                                                // align="center"
                                                                sx={{
                                                                    bgcolor: "#f5f5f5",
                                                                    fontSize: "12px",
                                                                    fontWeight: 500,
                                                                    position: "sticky",
                                                                    top: 59,
                                                                    zIndex: 2,
                                                                    borderRight: "1px solid lightgrey",
                                                                }}
                                                            >
                                                                Corporation
                                                            </CustomTableCell>
                                                        </TableRow>

                                                    </TableHead>
                                                    <TableBody>
                                                        {tableData.data[sectionKey].map((row, i) => (
                                                            <TableRow key={i}>
                                                                <CustomTableCell
                                                                    sx={{
                                                                        fontSize: "10px",
                                                                        fontWeight: i === tableData.data[sectionKey].length - 1 ? "bold" : 500,
                                                                        bgcolor: i % 2 === 0 ? "#ffffff" : "#e7e7e7",
                                                                        minWidth: `${corpWidth}px`,
                                                                        maxWidth: `${corpWidth}px`,
                                                                        borderRight: "1px solid lightgrey",
                                                                    }}
                                                                >
                                                                    {formatCellValue(row[firstStickyKey]?.corporation || "")}
                                                                </CustomTableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </Box>
                                        )}
                                        <Box key={sectionIndex} className="data-table" >
                                            <Table size='small' key={sectionIndex}
                                                {...(condition && !isSideBySide && tableData?.selected_option
                                                    ? {
                                                        ref: (el) => (tableRefs.current[sectionIndex] = el),
                                                        sx: {
                                                            width: maxTableWidth ? `${maxTableWidth}px` : "auto",
                                                        },
                                                    }
                                                    : {})}

                                            >
                                                <TableHead>
                                                    {/* main headers for side by side */}
                                                    {Object.keys(tableData.data[sectionKey][0]).some(key =>
                                                        sections.includes(key)) && isSideBySide && (
                                                            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                                                                {Object.keys(tableData.data[sectionKey][0]).map((mainKey, index) => {
                                                                    if (sections.includes(mainKey)) {
                                                                        const row = tableData.data[sectionKey][0];
                                                                        return (
                                                                            <CustomTableCell
                                                                                key={index}
                                                                                colSpan={10}
                                                                                sx={{
                                                                                    fontSize: "14px",
                                                                                    fontWeight: "bold",
                                                                                    position: "sticky",
                                                                                    top: 0,
                                                                                    zIndex: 4,
                                                                                    // borderRadius: (isSideBySide && Object.keys(tableData.data)[0]) ? "0px 8px 0px 0px" : "8px 8px 0px 0px",
                                                                                }}
                                                                                align="center"
                                                                                isMainHeader
                                                                                isFirstHeader={mainKey}

                                                                            >
                                                                                {renderHeaderTitle(mainKey, row)}
                                                                            </CustomTableCell>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })
                                                                }
                                                            </TableRow>
                                                        )}

                                                    {/* Existing headers row */}
                                                    <TableRow sx={{ bgcolor: "#ffffff" }}>
                                                        {Object.keys(tableData.data[sectionKey][0])
                                                            .filter(value => {
                                                                if (skipKeys.includes(value)) return false;
                                                                const nestedValue = tableData.data[sectionKey][0][value];
                                                                if (["month_to_date", "year_to_date"].includes(value)) {
                                                                    return nestedValue && Object.keys(nestedValue).length > 0;
                                                                }
                                                                return true;
                                                            })
                                                            .map((mainKey, index) => {
                                                                const row = tableData.data[sectionKey][0];

                                                                const subKeys = typeof tableData.data[sectionKey][0][mainKey] === "object"
                                                                    ? Object.keys(tableData.data[sectionKey][0][mainKey])
                                                                    : null;
                                                                const computedColSpan =
                                                                    subKeys
                                                                        ? subKeys.length -
                                                                        (isSideBySide && subKeys.includes("corporation") ? 1 : 0)
                                                                        : 1;

                                                                if (computedColSpan === 0 && isSideBySide) return null;
                                                                return (
                                                                    <CustomTableCell
                                                                        key={index}
                                                                        colSpan={computedColSpan}
                                                                        // colSpan={subKeys ? subKeys.length : 1}
                                                                        // colSpan={
                                                                        //     subKeys
                                                                        //         ? subKeys.length -
                                                                        //         (isSideBySide && subKeys.includes("corporation") ? 1 : 0)
                                                                        //         : 0
                                                                        // }
                                                                        sx={{
                                                                            height: isSideBySide ? "33px" : 25,
                                                                            fontSize: "12px",
                                                                            fontWeight: 600,
                                                                            position: "sticky",
                                                                            top: isSideBySide ? 27 : 0,
                                                                            zIndex: 3,
                                                                            ...(sections.includes(mainKey)
                                                                                && isSideBySide && {
                                                                                backgroundColor: "#f5f5f5",
                                                                            }),


                                                                            ...(index === 0 && !isSideBySide && {
                                                                                width: "120px",
                                                                                minWidth: "120px",
                                                                                maxWidth: "120px",
                                                                                position: "sticky",
                                                                                left: 0,
                                                                                zIndex: 5
                                                                            })

                                                                        }}
                                                                        align="center"
                                                                        isFirstHeader={mainKey}
                                                                        isMainHeader
                                                                        isLastInGroup={subKeys}
                                                                    >
                                                                        {
                                                                            (sections.includes(mainKey) && isSideBySide)
                                                                                ? "\u00A0"
                                                                                : renderHeaderTitle(mainKey, row)
                                                                        }

                                                                    </CustomTableCell>
                                                                );
                                                            })}
                                                    </TableRow>

                                                    {/* sub headers row */}
                                                    <TableRow>
                                                        {Object.keys(tableData.data[sectionKey][0]).flatMap((mainKey, index) => {
                                                            const subKeys = typeof tableData.data[sectionKey][0][mainKey] === "object"
                                                                ?
                                                                Object.keys(tableData.data[sectionKey][0][mainKey])
                                                                :
                                                                [mainKey];
                                                            return subKeys
                                                                // .filter(value => !skipKeys.includes(value))
                                                                .filter(value => !(skipKeys.includes(value) || (isSideBySide && value === "corporation")))
                                                                .map((subKey, subIndex) => (
                                                                    <CustomTableCell key={`${mainKey}-${subIndex}`}
                                                                        sx={{
                                                                            fontSize: "12px",
                                                                            fontWeight: 500,
                                                                            position: "sticky",
                                                                            top: isSideBySide ? 59 : 27,
                                                                            zIndex: 2,
                                                                            backgroundColor: "#f5f5f5",

                                                                            border: "1px solid lightgrey",
                                                                            ...(
                                                                                // (isSideBySide && subKey === "corporation")
                                                                                //     ? {
                                                                                //         position: "sticky",
                                                                                //         left: 0,
                                                                                //         zIndex: 5,
                                                                                //         ...stickyBorder,
                                                                                //         width: `${corpWidth}px`,
                                                                                //         minWidth: `${corpWidth}px`,
                                                                                //         maxWidth: `${corpWidth}px`,
                                                                                //     }
                                                                                //     :
                                                                                (!isSideBySide && requiredKeys.includes(subKey)) && {
                                                                                    position: "sticky",
                                                                                    left: subIndex === 0
                                                                                        ? 0
                                                                                        : `${corpWidth + (subIndex - 1) * 80}px`,
                                                                                    zIndex: 5,
                                                                                    ...stickyBorder,
                                                                                    ...(subIndex === 0
                                                                                        ? {
                                                                                            width: `${corpWidth}px`,
                                                                                            minWidth: `${corpWidth}px`,
                                                                                            maxWidth: `${corpWidth}px`,
                                                                                        }
                                                                                        : {
                                                                                            width: "80px",
                                                                                            minWidth: "80px",
                                                                                            maxWidth: "80px",
                                                                                        }),
                                                                                }
                                                                            )


                                                                        }}
                                                                        isLastInGroup={subIndex === subKeys.length - 1}

                                                                    >
                                                                        {tableData.data[sectionKey]?.[0]?.[sectionKey]?.[subKey] === ""
                                                                            ? "\u00A0"
                                                                            : subKey === "occupancy"
                                                                                ? `${CustomTitle(subKey)} %`
                                                                                : subKey === "adr"
                                                                                    ? "ADR"
                                                                                    : subKey === "rev_par"
                                                                                        ? "Rev PAR"
                                                                                        : CustomTitle(subKey)}
                                                                    </CustomTableCell>
                                                                ));
                                                        })}
                                                    </TableRow>
                                                </TableHead>
                                                {/* body of table */}
                                                <TableBody
                                                //  sx={{ bgcolor: "#ffffff" }}
                                                >
                                                    {tableData.data[sectionKey].map((row, rowIndex) => {
                                                        const isLastRow = rowIndex === tableData.data[sectionKey].length - 1;
                                                        return (
                                                            <TableRow key={rowIndex}>
                                                                {Object.keys(row).flatMap((mainKey, index) => {

                                                                    if (skipKeys.includes(mainKey)) return [];
                                                                    const subValues = typeof row[mainKey] === "object"
                                                                        ?
                                                                        Object.values(row[mainKey])
                                                                        :
                                                                        [row[mainKey]];



                                                                    return subValues
                                                                        // .filter(value =>
                                                                        //     !skipKeys.includes(value) 
                                                                        // )
                                                                        .filter(value =>
                                                                            !skipKeys.includes(value) &&
                                                                            !(isSideBySide && corporationNames.includes(value))
                                                                        )
                                                                        .map((value, subIndex) => (
                                                                            <CustomTableCell key={`${rowIndex}-${mainKey}-${subIndex}`}
                                                                                sx={{
                                                                                    fontSize: "10px",
                                                                                    // fontWeight: 500,
                                                                                    fontWeight: isLastRow ? "bold" : 500,
                                                                                    bgcolor: isLastRow
                                                                                        ? "#f5f5f5"
                                                                                        : rowIndex % 2 === 0
                                                                                            ? "#ffffff"
                                                                                            : "#e7e7e7",

                                                                                    // ...(index === 0 && {
                                                                                    //     ...(isSideBySide
                                                                                    //         ?
                                                                                    //         (subIndex === 0 && {
                                                                                    //             position: "sticky",
                                                                                    //             left: 0,
                                                                                    //             zIndex: 0,
                                                                                    //             ...stickyBorder,
                                                                                    //             width: `${corpWidth}px`,
                                                                                    //             minWidth: `${corpWidth}px`,
                                                                                    //             maxWidth: `${corpWidth}px`,
                                                                                    //             bgcolor: isLastRow
                                                                                    //                 ? "#f5f5f5"
                                                                                    //                 : rowIndex % 2 === 0
                                                                                    //                     ? "#ffffff"
                                                                                    //                     : "#e7e7e7",
                                                                                    //         })
                                                                                    //         :
                                                                                    //         {
                                                                                    //             bgcolor: isLastRow
                                                                                    //                 ? "#f5f5f5"
                                                                                    //                 : rowIndex % 2 === 0
                                                                                    //                     ? "#ffffff"
                                                                                    //                     : "#e7e7e7",
                                                                                    //             position: "sticky",
                                                                                    //             left: subIndex === 0
                                                                                    //                 ? 0
                                                                                    //                 : `${corpWidth + (subIndex - 1) * 80}px`,
                                                                                    //             zIndex: 0,
                                                                                    //             ...stickyBorder,
                                                                                    //             ...(subIndex === 0
                                                                                    //                 ? {
                                                                                    //                     width: `${corpWidth}px`,
                                                                                    //                     minWidth: `${corpWidth}px`,
                                                                                    //                     maxWidth: `${corpWidth}px`,
                                                                                    //                 }
                                                                                    //                 : {
                                                                                    //                     width: "80px",
                                                                                    //                     minWidth: "80px",
                                                                                    //                     maxWidth: "80px",
                                                                                    //                 }),
                                                                                    //         }),
                                                                                    // })
                                                                                    ...(index === 0 && !isSideBySide && {
                                                                                        bgcolor: isLastRow
                                                                                            ? "#f5f5f5"
                                                                                            : rowIndex % 2 === 0
                                                                                                ? "#ffffff"
                                                                                                : "#e7e7e7",
                                                                                        position: "sticky",
                                                                                        left: subIndex === 0
                                                                                            ? 0
                                                                                            : `${corpWidth + (subIndex - 1) * 80}px`,
                                                                                        zIndex: 0,
                                                                                        ...stickyBorder,
                                                                                        ...(subIndex === 0
                                                                                            ? {
                                                                                                width: `${corpWidth}px`,
                                                                                                minWidth: `${corpWidth}px`,
                                                                                                maxWidth: `${corpWidth}px`,
                                                                                            }
                                                                                            : {
                                                                                                width: "80px",
                                                                                                minWidth: "80px",
                                                                                                maxWidth: "80px",
                                                                                            }),
                                                                                    })

                                                                                }}
                                                                                isLastInGroup={subIndex === subValues.length - 1}
                                                                            // align={isSideBySide ? (index === 0 ? "left" : "center") : "left"}
                                                                            >
                                                                                {formatCellValue(value)}
                                                                            </CustomTableCell>
                                                                        ));
                                                                })}
                                                            </TableRow>
                                                        )
                                                    }

                                                    )}
                                                </TableBody>
                                            </Table>
                                        </Box>
                                    </>
                                )
                            })
                            :
                            <Stack alignItems="center" justifyContent="center" height="100%" width="100%">
                                <Typography fontWeight={500} color="text.secondary">
                                    {
                                        tableData?.data && tableData?.data?.message
                                            ? tableData.data.message
                                            : "No Sale Data available"
                                    }
                                </Typography>
                            </Stack>

                        // <Loader loading={loading} />
                    }
                </Stack>
                {!showCustomScreen && <Typography sx={{ textAlign: "right", width: "100%" }} fontWeight={400} fontSize={11}>{getESTTime()}</Typography>}

                {open &&
                    <CustomizeDialog
                        open={open}
                        onClose={() => setOpen(false)}
                        handleData={handleUpdatedData}
                        data={customeDataSelect}
                        defaultDetails={defaultDetails}
                        exportFormate={setFileExportData}
                    />
                }
                <Loader loading={loading} />
            </Stack>
        </SnackbarProvider >
    );
}

export default FlashReport;
